import Hat from './hat';
import * as PrefabBuilder from '../../utils/PrefabBuilder';
import {game} from '../../Game';
import {Settings} from '../../Settings';
import {
	getPortalHatMuzzle,
	PORTAL_HAT_OFFSET_ANGLE,
	PORTAL_HAT_OFFSET_LENGTH,
} from './portalHatMath';
import {PortalImpactBurst, PortalMuzzleFlash} from '../guns/PortalEffects';
import {findPortalPlacementOverlap, resolvePortalPlacement} from '../guns/portalCollision';
import * as AudioManager from '../../utils/AudioManager';
import * as TutorialManager from '../../utils/TutorialManager';
import {placePortalBehindDisplays} from '../guns/portalDisplayOrder';
import {PORTAL_APERTURE_HALF_LENGTH} from '../guns/portalMath';

// PortalHelmet0000 is 106x107. The firing point is tuned to 53px forward and
// 40px above the centered sprite origin; see portalHatMath.js.
const SHOOT_FORCE = 1200;
const SHOOT_DELAY = 350;

let nextPortalHatId = 0;

export class PortalHat extends Hat {
	constructor(character, head, body) {
		super(character, head, body);
		this.texture = 'PortalHelmet0000';
		this.hatOffsetLength = PORTAL_HAT_OFFSET_LENGTH;
		this.hatOffsetAngle = PORTAL_HAT_OFFSET_ANGLE;
		this.isPortalHat = true;
		this.isTriggerHat = true;
		this.colorA = '#0093ff';
		this.colorB = '#ff9800';
		this.activePortals = [];
		this.activeProjectile = null;
		this.shootTimer = SHOOT_DELAY;
		this.portalPairId = `portal-hat-${nextPortalHatId++}`;
		this.nextPortalColorIndex = 0;
		this.muzzleFlashes = [];
		this.impactBursts = [];
		this.detached = false;
		this.attach();
	}

	attach() {
		super.attach();
		TutorialManager.showTutorial(TutorialManager.TUTORIALS.PORTALGUN);
		this.detached = false;
		this.hatBody._portalGunOwner = this;
		AudioManager.preloadSFX('portal_shoot');
	}

	ownsBody(body) {
		if(!body) return false;
		if(body === this.hatBody || body.mainCharacter) return true;
		if(this.character.lookupObject?._bodies?.includes(body)) return true;
		return this.character.vehicleParts?.includes(body) || false;
	}

	getMuzzle() {
		const position = this.hatBody.GetPosition();
		return getPortalHatMuzzle(
			{x: position.get_x(), y: position.get_y()},
			this.hatBody.GetAngle(),
			this.character.flipped,
			Settings.PTM,
		);
	}

	activate() {
		if(this.detached || !this.hatBody || this.shootTimer < SHOOT_DELAY) return;
		this.shoot();
	}

	shoot() {
		if(this.activeProjectile && !this.activeProjectile.destroyed) return;
		const muzzle = this.getMuzzle();
		const color = this.nextPortalColorIndex === 0 ? this.colorA : this.colorB;
		const spawnPosition = new Box2D.b2Vec2(muzzle.position.x, muzzle.position.y);
		const prefabData = PrefabBuilder.generatePrefab(
			spawnPosition,
			muzzle.angle * game.editor.RAD2DEG,
			'PortalBullet',
			false,
		);
		const bulletBody = prefabData.lookupObject._bodies[0];
		prefabData.prefabClass.setOwner(this, color);
		prefabData.prefabClass.init();
		this.activeProjectile = prefabData.prefabClass;
		bulletBody.SetLinearVelocity(this.hatBody.GetLinearVelocity());

		// continuous collision detection for fast-moving projectiles
		bulletBody.SetBullet(true);

		const force = new Box2D.b2Vec2(
			Math.cos(muzzle.angle) * (SHOOT_FORCE / game.editor.editorSettingsObject.gameSpeed),
			Math.sin(muzzle.angle) * (SHOOT_FORCE / game.editor.editorSettingsObject.gameSpeed),
		);
		bulletBody.ApplyForce(force, bulletBody.GetWorldCenter(), true);
		this.createMuzzleFlash(muzzle, color);
		AudioManager.playSFX('portal_shoot', 0.35, 1, muzzle.position, true);
		Box2D.destroy(force);
		Box2D.destroy(spawnPosition);
		this.shootTimer = 0;
	}

	createMuzzleFlash(muzzle, color) {
		const anchor = this.hatBody?.myTexture;
		const parent = anchor?.parent;
		if(!parent) return;
		this.muzzleFlashes.push(new PortalMuzzleFlash({
			parent,
			anchor,
			position: {
				x: muzzle.position.x * Settings.PTM,
				y: muzzle.position.y * Settings.PTM,
			},
			angle: muzzle.angle,
			color,
		}));
	}

	// A shot that struck a surface which cannot hold a portal splashes there.
	createImpactBurst(position, angle, color) {
		const parent = this.hatBody?.myTexture?.parent;
		if(!parent || !position) return null;
		const burst = new PortalImpactBurst({
			parent,
			position: {
				x: position.x * Settings.PTM,
				y: position.y * Settings.PTM,
			},
			angle,
			color,
		});
		(this.impactBursts ??= []).push(burst);
		return burst;
	}

	placePortalBehindCharacter(portal) {
		const lookupObject = this.character?.lookupObject;
		const characterDisplays = [
			...(lookupObject?._bodies || []).flatMap(body => [
				body?.mySprite,
				body?.myTexture,
			]),
			...(lookupObject?._textures || []),
			this.hatBody?.mySprite,
			this.hatBody?.myTexture,
		];
		return placePortalBehindDisplays(portal?.body?.mySprite, characterDisplays);
	}

	spawnPortal(
		worldPosition,
		angle,
		hostBody = null,
		hostFixture = null,
		projectileColor = null,
		hostChildIndex = null,
	) {
		if(this.detached) return null;
		const placement = resolvePortalPlacement(
			Box2D,
			hostFixture,
			hostChildIndex,
			worldPosition,
			angle,
			PORTAL_APERTURE_HALF_LENGTH,
		);
		// Invalid shots consume the projectile without replacing a valid endpoint.
		if(!placement) return null;
		this.activePortals = this.activePortals.filter(portal => !portal.destroyed);
		let replacedColor = null;
		// The projectile color is also its portal channel. Replacing the matching
		// endpoint keeps overlapping shots from ever producing two same-color exits.
		let replaceIndex = projectileColor ? this.activePortals.findIndex(
			portal => portal.prefabObject.settings.color === projectileColor,
		) : -1;
		if(replaceIndex < 0 && this.activePortals.length >= 2) replaceIndex = 0;
		// Validate the snapped placement against every portal, including an
		// unpaired first shot. Check before destroying the endpoint being replaced.
		if(findPortalPlacementOverlap(Box2D, game.world, placement, this.activePortals[replaceIndex])) {
			return null;
		}
		if(replaceIndex >= 0) {
			const [replacedPortal] = this.activePortals.splice(replaceIndex, 1);
			replacedColor = replacedPortal.prefabObject.settings.color;
			replacedPortal.destroy();
		}
		const color = projectileColor || replacedColor ||
			(this.activePortals.length ? this.colorB : this.colorA);

		const position = new Box2D.b2Vec2(
			placement.position.x,
			placement.position.y,
		);
		const prefabData = PrefabBuilder.generatePrefab(
			position,
			placement.angle * game.editor.RAD2DEG,
			'Portal',
			false,
		);
		Box2D.destroy(position);
		Object.assign(prefabData.prefabClass.prefabObject.settings, {
			pairId: this.portalPairId,
			color,
		});
		// Shot portals stay audible offscreen so far shots still give feedback.
		prefabData.prefabClass.fromPortalGun = true;
		prefabData.prefabClass.init();
		prefabData.prefabClass.attachToHost(
			placement.hostBody,
			placement.hostFixture,
			placement.hostFixtures,
		);
		this.placePortalBehindCharacter(prefabData.prefabClass);
		this.activePortals.push(prefabData.prefabClass);
		// Failed impacts and expired projectiles keep the selected channel.
		this.nextPortalColorIndex = color === this.colorA ? 1 : 0;
		return prefabData.prefabClass;
	}

	update() {
		this.shootTimer += game.editor.deltaTime;
		for(const effects of [this.muzzleFlashes, this.impactBursts ?? []]) {
			for(let index = effects.length - 1; index >= 0; index--) {
				const effect = effects[index];
				if(effect.update(game.editor.deltaTimeSeconds)) continue;
				effect.destroy();
				effects.splice(index, 1);
			}
		}
	}

	destroyMuzzleFlashes() {
		for(const effects of [this.muzzleFlashes, this.impactBursts ?? []]) {
			for(const effect of effects) effect.destroy();
			effects.length = 0;
		}
	}

	destroyPortals() {
		for(const portal of [...this.activePortals]) portal.destroy();
		this.activePortals.length = 0;
	}

	detach() {
		this.detached = true;
		this.activeProjectile?.destroy();
		this.activeProjectile = null;
		this.destroyMuzzleFlashes();
		this.destroyPortals();
		if(this.hatBody?._portalGunOwner === this) delete this.hatBody._portalGunOwner;
		if(this.hatWeldJoint) super.detach();
	}

	destroy() {
		this.destroyMuzzleFlashes();
		this.destroyPortals();
		super.destroy();
	}
}
